/**
 * My DSA Offers Page
 * DSA dashboard to manage their posted commission offers
 *
 * Features:
 * - View all posted offers with stats
 * - Filter by status, search, loan type, date range
 * - Backend pagination per tab
 * - Pause/Resume offers
 * - Edit and delete offers
 * - View analytics per offer
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Skeleton } from 'primereact/skeleton';
import { Dropdown } from 'primereact/dropdown';
import { SelectButton } from 'primereact/selectbutton';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { TabView, TabPanel } from 'primereact/tabview';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { Paginator } from 'primereact/paginator';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import {
    dsaOfferService,
    DSA_LOAN_TYPES,
} from '../../services/dsaOfferService';
import DsaOfferCard from './components/DsaOfferCard';
import NavigationTabMenu from '../../components/common/NavigationTabMenu';

// My Payouts section tabs
const MY_PAYOUTS_TABS = [
    { label: 'My Listed Payouts', icon: 'pi pi-list', route: '/app/dsa-offers/my-offers' },
    { label: 'Payout Enquiries', icon: 'pi pi-inbox', route: '/app/dsa-offers/received-invites' },
    { label: 'Post Payout', icon: 'pi pi-plus', route: '/app/dsa-offers/post' }
];

// Constants
const ROWS_PER_PAGE = 12;

const LOAN_TYPE_OPTIONS = [
    { label: 'All Types', value: '' },
    ...DSA_LOAN_TYPES.map(lt => ({ label: lt.label, value: lt.value }))
];

const MyOffers = () => {
    const navigate = useNavigate();
    const toast = useRef(null);

    // Refs to prevent duplicate API calls
    const prevFiltersRef = useRef(null);
    const lastFetchKeyRef = useRef('');
    const searchTimeoutRef = useRef(null);

    // State
    const [loading, setLoading] = useState(true);
    const [summary, setSummary] = useState({ total: 0, active: 0, paused: 0, expired: 0, total_invites: 0 });
    const [viewMode, setViewMode] = useState('grid');
    const [activeTab, setActiveTab] = useState(0);
    const [actionLoading, setActionLoading] = useState(null);

    // Filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [loanTypeFilter, setLoanTypeFilter] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    // Pagination state per tab
    const [allPagination, setAllPagination] = useState({ first: 0, rows: ROWS_PER_PAGE, total: 0 });
    const [activePagination, setActivePagination] = useState({ first: 0, rows: ROWS_PER_PAGE, total: 0 });
    const [pausedPagination, setPausedPagination] = useState({ first: 0, rows: ROWS_PER_PAGE, total: 0 });
    const [expiredPagination, setExpiredPagination] = useState({ first: 0, rows: ROWS_PER_PAGE, total: 0 });

    // Tab loaded flags
    const [allLoaded, setAllLoaded] = useState(false);
    const [activeLoaded, setActiveLoaded] = useState(false);
    const [pausedLoaded, setPausedLoaded] = useState(false);
    const [expiredLoaded, setExpiredLoaded] = useState(false);

    // Tab data
    const [allOffers, setAllOffers] = useState([]);
    const [activeOffers, setActiveOffers] = useState([]);
    const [pausedOffers, setPausedOffers] = useState([]);
    const [expiredOffers, setExpiredOffers] = useState([]);

    // Options
    const viewOptions = [
        { icon: 'pi pi-th-large', value: 'grid' },
        { icon: 'pi pi-list', value: 'list' }
    ];

    // Check if any filter is active
    const hasActiveFilters = debouncedSearch || loanTypeFilter || startDate || endDate;
    const activeFiltersCount = [debouncedSearch, loanTypeFilter, startDate, endDate].filter(Boolean).length;

    // Clear all filters
    const clearFilters = () => {
        setSearchQuery('');
        setDebouncedSearch('');
        setLoanTypeFilter('');
        setStartDate(null);
        setEndDate(null);
    };

    // Debounce search input
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        searchTimeoutRef.current = setTimeout(() => {
            if (searchQuery !== debouncedSearch) {
                setDebouncedSearch(searchQuery);
            }
        }, 500);

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchQuery, debouncedSearch]);

    // Get status for current tab
    const getStatusForTab = (tabIndex) => {
        switch (tabIndex) {
            case 1: return 'active';
            case 2: return 'paused';
            case 3: return 'expired';
            default: return null;
        }
    };

    // Get pagination for current tab
    const getPaginationForTab = (tabIndex) => {
        switch (tabIndex) {
            case 1: return activePagination;
            case 2: return pausedPagination;
            case 3: return expiredPagination;
            default: return allPagination;
        }
    };

    // Set pagination for current tab
    const setPaginationForTab = (tabIndex, pagination) => {
        switch (tabIndex) {
            case 1: setActivePagination(pagination); break;
            case 2: setPausedPagination(pagination); break;
            case 3: setExpiredPagination(pagination); break;
            default: setAllPagination(pagination);
        }
    };

    // Get loaded flag for current tab
    const getLoadedForTab = (tabIndex) => {
        switch (tabIndex) {
            case 1: return activeLoaded;
            case 2: return pausedLoaded;
            case 3: return expiredLoaded;
            default: return allLoaded;
        }
    };

    // Set loaded flag for current tab
    const setLoadedForTab = (tabIndex, loaded) => {
        switch (tabIndex) {
            case 1: setActiveLoaded(loaded); break;
            case 2: setPausedLoaded(loaded); break;
            case 3: setExpiredLoaded(loaded); break;
            default: setAllLoaded(loaded);
        }
    };

    // Get offers for current tab
    const getOffersForTab = (tabIndex) => {
        switch (tabIndex) {
            case 1: return activeOffers;
            case 2: return pausedOffers;
            case 3: return expiredOffers;
            default: return allOffers;
        }
    };

    // Set offers for current tab
    const setOffersForTab = (tabIndex, offersData) => {
        switch (tabIndex) {
            case 1: setActiveOffers(offersData); break;
            case 2: setPausedOffers(offersData); break;
            case 3: setExpiredOffers(offersData); break;
            default: setAllOffers(offersData);
        }
    };

    // Fetch offers for current tab
    const fetchOffersForTab = async (tabIndex, paginationOverride = null) => {
        const status = getStatusForTab(tabIndex);
        const pagination = paginationOverride || getPaginationForTab(tabIndex);

        const filters = {
            per_page: pagination.rows,
            page: Math.floor(pagination.first / pagination.rows) + 1
        };

        if (status) filters.status = status;
        if (debouncedSearch) filters.search = debouncedSearch;
        if (loanTypeFilter) filters.loan_type = loanTypeFilter;
        if (startDate) filters.date_from = startDate.toISOString().split('T')[0];
        if (endDate) filters.date_to = endDate.toISOString().split('T')[0];

        // Create fetch key to prevent duplicate calls
        const fetchKey = JSON.stringify({ ...filters, tabIndex });
        if (fetchKey === lastFetchKeyRef.current) {
            return;
        }
        lastFetchKeyRef.current = fetchKey;

        setLoading(true);
        try {
            const response = await dsaOfferService.getMyOffers(filters);

            if (response.success) {
                const data = response.data || {};
                const offersData = data.offers || [];
                const paginationData = data.pagination || {};

                setOffersForTab(tabIndex, offersData);
                setPaginationForTab(tabIndex, {
                    ...pagination,
                    total: paginationData.total || 0
                });
                setSummary(data.summary || null);
                setLoadedForTab(tabIndex, true);
            }
        } catch (error) {
            console.error('Failed to fetch offers:', error);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to load your offers'
            });
        } finally {
            setLoading(false);
        }
    };

    // Single effect for fetching - handles filter changes and tab changes
    useEffect(() => {
        const currentFilters = {
            debouncedSearch,
            loanTypeFilter,
            startDate: startDate?.toISOString() || null,
            endDate: endDate?.toISOString() || null
        };

        const prevFilters = prevFiltersRef.current;

        // Check if any filter changed (not just tab)
        const filtersChanged = prevFilters !== null && (
            prevFilters.debouncedSearch !== debouncedSearch ||
            prevFilters.loanTypeFilter !== loanTypeFilter ||
            prevFilters.startDate !== (startDate?.toISOString() || null) ||
            prevFilters.endDate !== (endDate?.toISOString() || null)
        );

        // Update ref
        prevFiltersRef.current = currentFilters;

        if (filtersChanged) {
            // Filters changed - reset all tabs and fetch current tab from page 1
            setAllLoaded(false);
            setActiveLoaded(false);
            setPausedLoaded(false);
            setExpiredLoaded(false);

            // Reset pagination to first page
            const resetPagination = { first: 0, rows: ROWS_PER_PAGE, total: 0 };
            setAllPagination(resetPagination);
            setActivePagination(resetPagination);
            setPausedPagination(resetPagination);
            setExpiredPagination(resetPagination);

            // Clear last fetch key to force new fetch
            lastFetchKeyRef.current = '';

            // Fetch with reset pagination
            fetchOffersForTab(activeTab, resetPagination);
        } else if (!getLoadedForTab(activeTab)) {
            // Tab changed or initial load - fetch if not loaded
            fetchOffersForTab(activeTab);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, debouncedSearch, loanTypeFilter, startDate, endDate]);

    // Handle tab change
    const handleTabChange = (e) => {
        setActiveTab(e.index);
    };

    // Handle page change
    const handlePageChange = (tabIndex, event) => {
        const newPagination = {
            ...getPaginationForTab(tabIndex),
            first: event.first,
            rows: event.rows
        };
        setPaginationForTab(tabIndex, newPagination);
        setLoadedForTab(tabIndex, false);
        // Clear last fetch key and fetch with new pagination
        lastFetchKeyRef.current = '';
        fetchOffersForTab(tabIndex, newPagination);
    };

    // Handle pause offer
    const handlePause = async (offer, e) => {
        e?.stopPropagation();
        setActionLoading(offer.id);
        try {
            const response = await dsaOfferService.pauseOffer(offer.id);
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Offer Paused',
                    detail: 'Your offer is now hidden from the marketplace'
                });
                // Reset all tabs to refetch
                setAllLoaded(false);
                setActiveLoaded(false);
                setPausedLoaded(false);
                lastFetchKeyRef.current = '';
                fetchOffersForTab(activeTab);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to pause offer'
            });
        } finally {
            setActionLoading(null);
        }
    };

    // Handle resume offer
    const handleResume = async (offer, e) => {
        e?.stopPropagation();
        setActionLoading(offer.id);
        try {
            const response = await dsaOfferService.resumeOffer(offer.id);
            if (response.success) {
                toast.current?.show({
                    severity: 'success',
                    summary: 'Offer Resumed',
                    detail: 'Your offer is now visible in the marketplace'
                });
                // Reset all tabs to refetch
                setAllLoaded(false);
                setActiveLoaded(false);
                setPausedLoaded(false);
                lastFetchKeyRef.current = '';
                fetchOffersForTab(activeTab);
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: error.message || 'Failed to resume offer'
            });
        } finally {
            setActionLoading(null);
        }
    };

    // Handle edit
    const handleEdit = (offer, e) => {
        e?.stopPropagation();
        navigate(`/app/dsa-offers/edit/${offer.id}`);
    };

    // Handle delete
    const handleDelete = (offer, e) => {
        e?.stopPropagation();
        confirmDialog({
            message: `Are you sure you want to delete the offer for "${offer.bank_name}"? This action cannot be undone.`,
            header: 'Delete Offer',
            icon: 'pi pi-exclamation-triangle',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                setActionLoading(offer.id);
                try {
                    const response = await dsaOfferService.deleteOffer(offer.id);
                    if (response.success) {
                        toast.current?.show({
                            severity: 'success',
                            summary: 'Offer Deleted',
                            detail: 'Your offer has been removed'
                        });
                        // Reset all tabs to refetch
                        setAllLoaded(false);
                        setActiveLoaded(false);
                        setPausedLoaded(false);
                        setExpiredLoaded(false);
                        lastFetchKeyRef.current = '';
                        fetchOffersForTab(activeTab);
                    }
                } catch (error) {
                    toast.current?.show({
                        severity: 'error',
                        summary: 'Error',
                        detail: error.message || 'Failed to delete offer'
                    });
                } finally {
                    setActionLoading(null);
                }
            }
        });
    };

    // Handle view details
    const handleView = (offer) => {
        navigate(`/app/dsa-offers/${offer.id}`);
    };

    // Handle refresh
    const handleRefresh = () => {
        lastFetchKeyRef.current = '';
        setLoadedForTab(activeTab, false);
        fetchOffersForTab(activeTab);
    };

    // ── Loading skeleton — matches the new DsaOfferCard shape ──
    const renderSkeleton = () => (
        <div className="grid">
            {[1, 2, 3, 4, 5, 6, 7, 8].map(i => (
                <div key={i} className="col-12 md:col-6 lg:col-3 p-2">
                    <div className="surface-card border-round border-1 surface-border p-3 h-full">
                        <div className="flex align-items-center gap-2 mb-2">
                            <Skeleton shape="circle" size="2.25rem" />
                            <div className="flex-1">
                                <Skeleton width="70%" height="1rem" className="mb-1" />
                                <Skeleton width="50%" height="0.7rem" />
                            </div>
                        </div>
                        <Skeleton width="80%" height="0.75rem" className="mb-3" />
                        <Skeleton width="100%" height="9rem" className="mb-2" />
                        <Skeleton width="100%" height="2.25rem" className="mb-2" />
                        <Skeleton width="100%" height="2rem" />
                    </div>
                </div>
            ))}
        </div>
    );

    // ── Empty state ──
    const renderEmpty = (status) => (
        <div className="flex flex-column align-items-center justify-content-center py-6">
            <i className="pi pi-inbox text-400 pi-2x mb-3" aria-hidden="true" />
            <h4 className="text-900 font-medium m-0 mb-1 text-base">
                {status === 'active' ? 'No active offers'
                : status === 'paused' ? 'No paused offers'
                : status === 'expired' ? 'No expired offers'
                : 'You haven’t posted any offers yet'}
            </h4>
            <p className="text-500 text-sm text-center mb-3" style={{ maxWidth: '320px' }}>
                {hasActiveFilters
                    ? 'Try adjusting your filters or search terms.'
                    : 'Post your first DSA commission offer to start receiving enquiries.'}
            </p>
            {hasActiveFilters ? (
                <Button label="Clear filters" icon="pi pi-filter-slash" size="small" outlined severity="secondary" onClick={clearFilters} />
            ) : (
                <Button label="Post Payout" icon="pi pi-plus" size="small" onClick={() => navigate('/app/dsa-offers/post')} />
            )}
        </div>
    );

    // ── Tab content: skeleton / empty / grid+paginator / list+paginator ──
    const renderOffers = (tabIndex) => {
        const offersToRender = getOffersForTab(tabIndex);
        const pagination = getPaginationForTab(tabIndex);
        const status = getStatusForTab(tabIndex);

        if (loading && !getLoadedForTab(tabIndex)) {
            return renderSkeleton();
        }
        if (offersToRender.length === 0) {
            return renderEmpty(status);
        }

        return (
            <>
                {viewMode === 'list' ? (
                    <div className="surface-card border-round border-1 surface-border overflow-hidden mb-3">
                        {offersToRender.map((offer) => (
                            <DsaOfferCard
                                key={offer.id}
                                offer={offer}
                                isOwner
                                viewMode="list"
                                onEdit={handleEdit}
                                onPause={handlePause}
                                onResume={handleResume}
                                onDelete={handleDelete}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="grid mb-3">
                        {offersToRender.map(offer => (
                            <div key={offer.id} className="col-12 md:col-6 lg:col-4 xl:col-3 p-2">
                                <DsaOfferCard
                                    offer={offer}
                                    isOwner
                                    onEdit={handleEdit}
                                    onPause={handlePause}
                                    onResume={handleResume}
                                    onDelete={handleDelete}
                                />
                            </div>
                        ))}
                    </div>
                )}

                {/* Paginator — trust the default (P2) */}
                {pagination.total > pagination.rows && (
                    <Paginator
                        first={pagination.first}
                        rows={pagination.rows}
                        totalRecords={pagination.total || 0}
                        onPageChange={(e) => handlePageChange(tabIndex, e)}
                        template="PrevPageLink PageLinks NextPageLink CurrentPageReport"
                        currentPageReportTemplate="{first}-{last} of {totalRecords}"
                        paginatorClassName="text-sm"
                        pageLinkSize={3}
                        className="surface-card border-round shadow-1"
                    />
                )}
            </>
        );
    };

    return (
        <div className="grid">
            <div className="col-12">
                <NavigationTabMenu tabs={MY_PAYOUTS_TABS} />
                <Toast ref={toast} />
                <ConfirmDialog />

                {/* Page header — plain div per P20, NOT inside a Card */}
                <div className="flex flex-column md:flex-row md:justify-content-between md:align-items-center gap-3 mb-3">
                    <div>
                        <h1 className="text-xl font-semibold text-900 m-0 mb-1">My Payout Offers</h1>
                        <p className="text-600 text-sm m-0">Manage the commission offers you have posted and track enquiries.</p>
                    </div>
                    <div className="flex flex-wrap align-items-center gap-2">
                        <Button label="Browse" icon="pi pi-search" outlined severity="secondary" size="small" onClick={() => navigate('/app/dsa-offers')} />
                        <Button label="Post Payout" icon="pi pi-plus" size="small" onClick={() => navigate('/app/dsa-offers/post')} />
                    </div>
                </div>

                {/* Stats KPI strip (P15 uniform cards; semantic colour ONLY on the value) */}
                <div className="grid mb-3">
                    <div className="col-6 md:col-3">
                        <div className="surface-card border-round shadow-1 p-3 h-full">
                            <div className="text-500 text-xs font-medium mb-1">Total Offers</div>
                            <div className="text-900 text-xl font-semibold">{summary.total || 0}</div>
                        </div>
                    </div>
                    <div className="col-6 md:col-3">
                        <div className="surface-card border-round shadow-1 p-3 h-full">
                            <div className="text-500 text-xs font-medium mb-1">Active</div>
                            <div className="text-green-600 text-xl font-semibold">{summary.active || 0}</div>
                        </div>
                    </div>
                    <div className="col-6 md:col-3">
                        <div className="surface-card border-round shadow-1 p-3 h-full">
                            <div className="text-500 text-xs font-medium mb-1">Paused</div>
                            <div className="text-orange-500 text-xl font-semibold">{summary.paused || 0}</div>
                        </div>
                    </div>
                    <div className="col-6 md:col-3">
                        <div className="surface-card border-round shadow-1 p-3 h-full">
                            <div className="text-500 text-xs font-medium mb-1">Total Enquiries</div>
                            <div className="text-blue-600 text-xl font-semibold">{summary.total_invites || 0}</div>
                        </div>
                    </div>
                </div>

                {/* Filter card — search + loan type + date range + view toggle */}
                <div className="surface-card border-round shadow-1 p-3 mb-3">
                    <div className="grid">
                        <div className="col-12 md:col-6 lg:col-4">
                            <IconField iconPosition="left" className="w-full">
                                <InputIcon className="pi pi-search" />
                                <InputText
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    placeholder="Search by bank, loan type..."
                                    className="w-full p-inputtext-sm"
                                />
                            </IconField>
                        </div>
                        <div className="col-6 md:col-3 lg:col-2">
                            <Dropdown
                                value={loanTypeFilter}
                                options={LOAN_TYPE_OPTIONS}
                                onChange={(e) => setLoanTypeFilter(e.value)}
                                optionLabel="label" optionValue="value"
                                placeholder="All Types"
                                showClear
                                className="w-full p-inputtext-sm"
                            />
                        </div>
                        <div className="col-6 md:col-3 lg:col-3">
                            <Calendar
                                value={startDate}
                                onChange={(e) => setStartDate(e.value)}
                                placeholder="From date"
                                showIcon
                                className="w-full p-inputtext-sm"
                            />
                        </div>
                        <div className="col-12 md:col-12 lg:col-3">
                            <Calendar
                                value={endDate}
                                onChange={(e) => setEndDate(e.value)}
                                placeholder="To date"
                                showIcon
                                className="w-full p-inputtext-sm"
                            />
                        </div>
                    </div>
                    <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2 mt-3 pt-2 border-top-1 surface-border">
                        <span className="text-600 text-sm">
                            {loading ? 'Loading...' : `${getPaginationForTab(activeTab)?.total || 0} payout${(getPaginationForTab(activeTab)?.total || 0) !== 1 ? 's' : ''}`}
                            {activeFiltersCount > 0 && ` · ${activeFiltersCount} filter${activeFiltersCount > 1 ? 's' : ''}`}
                        </span>
                        <div className="flex align-items-center gap-2 flex-wrap">
                            {hasActiveFilters && (
                                <Button label="Clear" icon="pi pi-filter-slash" severity="secondary" outlined size="small" onClick={clearFilters} />
                            )}
                            <SelectButton
                                value={viewMode}
                                options={viewOptions}
                                onChange={(e) => e.value && setViewMode(e.value)}
                                optionLabel="icon"
                                itemTemplate={(opt) => <i className={opt.icon} aria-hidden="true" />}
                                className="hidden md:inline-flex"
                            />
                        </div>
                    </div>
                </div>

                {/* TabView — All / Active / Paused / Expired */}
                <TabView activeIndex={activeTab} onTabChange={handleTabChange}>
                    <TabPanel header={`All (${summary?.total || 0})`}>{renderOffers(0)}</TabPanel>
                    <TabPanel header={`Active (${summary?.active || 0})`}>{renderOffers(1)}</TabPanel>
                    <TabPanel header={`Paused (${summary?.paused || 0})`}>{renderOffers(2)}</TabPanel>
                    <TabPanel header={`Expired (${summary?.expired || 0})`}>{renderOffers(3)}</TabPanel>
                </TabView>
            </div>
        </div>
    );
};

export default MyOffers;
